import React, {
  ChangeEvent,
  createContext,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useCurrentProject } from '@material-bank/projects-utils';
import {
  AnalyticsEventName,
  CustomEventName,
  getUserAttributes,
} from '@mb/lib';
import indexOf from 'lodash/indexOf';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';

import { useSelector, useDispatch, batch } from 'react-redux';
import { config, constants } from '../config';
import { useSearchService } from '../hooks';
import { useFiltersContext } from './FiltersContext';
import { useUpdateEffect } from '../hooks/customHooks';
import {
  appFiltersSelector,
  searchToggleSelector,
  overrideParamsSelector,
  isBrandSwitchSelector,
} from '../redux/selectors/filtersSelector';
import {
  searchResultsSelector,
  searchPaginationSelector,
  searchOptionsSelector,
  collectionHeaderSelector,
} from '../redux/selectors/searchSelector';
import { currentSortOptionSelector } from '../redux/selectors/searchSortingSelector';
import {
  resetComparisonFeature,
  setCurrentCollectionTitle,
  setIsCompareTableShown,
} from '../redux/sliceCreators/commonSlice';
import {
  setSelectedMaterials,
  clearFilters,
  toggleFiltersSidebar,
  setSearchToggle,
  setAppFilters,
  setOverrideParams,
  setIsBrandSwitch,
} from '../redux/sliceCreators/filtersSlice';
import {
  setResults,
  setPagination,
  setSearchOptions,
  setIsSearching,
  setIsSearchQueryRelaxed,
  setIsSearchRedirect,
} from '../redux/sliceCreators/searchSlice';
import {
  setCurrentSortOption,
  setSortingList,
} from '../redux/sliceCreators/sortingSlice';

import { ProductSearchMaterial } from '../types';
import {
  getUrlParameter,
  checkStockAvailability,
  getUrlPageNumber,
  setUrlPageNumber,
  getInitialUrlData,
  setUrlSortOption,
} from '../utils';
import {
  isPartnersPage,
  getPartnersPageQuery,
  isBannersPage,
  scrollResultsToTop,
  removeCompareParam,
} from '../utils/helpers';

interface SearchArgs {
  suggesterType?: string | null;
  suggesterValue?: string | null;
  searchType?: string | null;
  predefinedIds?: string[];
  analyticsTitle?: string | null;
  initialRequest?: boolean;
  additionalFilters?: Record<string, unknown>;
  action?: string;
  page?: number;
  query?: string;
  sortBy?: string;
}

//read the search from the location search, to apply saved searches
//or shared links to searches
let initialSearchValue = getUrlParameter('q');
const partnersRoute = isPartnersPage() ? getPartnersPageQuery() : null;
if (partnersRoute != null) {
  initialSearchValue = getUrlParameter('q', partnersRoute);
}
const isBannersSearch = isBannersPage();

let initialPageNumber = 1;

// Read initial page number
const currentPageNumber = getUrlPageNumber();
if (currentPageNumber != null) {
  initialPageNumber = Number(currentPageNumber) ?? 1;
}

// get users email from local storage
export function getEmail() {
  const userData = getUserAttributes();
  return userData?.email;
}

const { searchActionsEnum } = constants;

export const SearchContextProvider = (props: any) => {
  const customerEmail = getEmail();
  const dispatch = useDispatch();
  const currentSortOption = useSelector(currentSortOptionSelector);
  const results = useSelector(searchResultsSelector);
  const pagination = useSelector(searchPaginationSelector);
  const searchOptions = useSelector(searchOptionsSelector);

  const appFilters = useSelector(appFiltersSelector);
  const searchToggle = useSelector(searchToggleSelector);
  const isBrandSwitch = useSelector(isBrandSwitchSelector);
  const overrideParams = useSelector(overrideParamsSelector);
  const collectionHeader = useSelector(collectionHeaderSelector);

  const { setGlobalFilters } = useFiltersContext();
  const searchService = useSearchService(overwriteSearchQuery);

  const prevAppFiltersRef = useRef(appFilters);

  const { currentProject } = useCurrentProject();

  // Only update reference if deeply changed
  if (!isEqual(appFilters, prevAppFiltersRef.current)) {
    prevAppFiltersRef.current = appFilters;
  }

  const localAppFilters = prevAppFiltersRef.current;

  useEffect(() => {
    initRequest(searchActionsEnum.init);
  }, []);

  const initRequest = async (action: string) => {
    await search({
      ...searchOptions,
      query: initialSearchValue,
      page: initialPageNumber || pagination?.currentPage,
      action: action,
      initialRequest: true,
    });
  };

  useEffect(() => {
    if (!isBannersSearch) {
      dispatch(
        setSearchOptions({
          query: initialSearchValue,
          page: initialPageNumber || pagination?.currentPage,
          action: searchActionsEnum.init,
          sortBy: null,
        }),
      );
    }

    dataLayer.push({
      event: AnalyticsEventName.VIEW_SEARCH_RESULTS,
      search: {
        method: initialSearchValue.length > 0 ? 'suggestion' : 'category',
        results: results.length,
        search_term: initialSearchValue,
      },
    });
  }, []);

  useEffect(() => {
    if (collectionHeader != null) {
      const images = collectionHeader?.additional_images || [];
      const headerImages = images.map((imgUrl: string) => ({
        image: [{ url: imgUrl }],
      }));

      const obj = {
        header_headline: collectionHeader?.name,
        header_description: collectionHeader?.description,
        header_inspiration_images: JSON.stringify(headerImages),
      };
      const collectionHeaderEvent = new CustomEvent('show-collection-header', {
        detail: obj,
      });
      document.dispatchEvent(collectionHeaderEvent);
    }
  }, [collectionHeader]);

  useEffect(() => {
    const collectionHeaderEvent = new CustomEvent(
      CustomEventName.LOADED_SEARCH_CONTEXT,
    );
    document.dispatchEvent(collectionHeaderEvent);
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', handleHistoryStateChange);
    return () => {
      window.removeEventListener('popstate', handleHistoryStateChange);
    };
  }, [appFilters, searchToggle, searchOptions]);

  useUpdateEffect(() => {
    scrollResultsToTop();
    if (overrideParams != null) {
      dispatch(setOverrideParams(null));
      return;
    }

    search({
      ...searchOptions,
      action:
        isEmpty(localAppFilters) || overrideParams != null
          ? searchActionsEnum.resetFilters
          : searchActionsEnum.filter,
      page: 1,
    });
  }, [
    localAppFilters,
    currentProject?.type,
    currentProject?.country,
    currentProject?.state,
  ]); // Re-run effect when currentProject

  useUpdateEffect(() => {
    // Do not send additional sortBy request, if there was request from search field
    if (
      currentSortOption == null &&
      searchOptions?.action === searchActionsEnum.searchBar
    ) {
      return;
    }

    search({
      ...searchOptions,
      action: searchActionsEnum.sortBy,
      page: 1,
    });
  }, [currentSortOption]);

  useUpdateEffect(() => {
    if (overrideParams == null) {
      return;
    }

    let overrideSort: any = null;
    for (const key in overrideParams) {
      if (key?.includes('sort.')) {
        overrideSort = {
          field: key?.split('sort.')?.[1],
          direction: overrideParams[key],
        };
        break;
      }
    }
    if (overrideSort != null) {
      dispatch(setCurrentSortOption(overrideSort));
      setTimeout(() => {
        setUrlSortOption(overrideSort, false);
      }, 300);
    }
  }, [overrideParams]);

  // Update Search criteria, if Browser History Back/Forward buttons clicked
  const handleHistoryStateChange = () => {
    window.scrollTo(0, 0);
    const { initialAppFilters, initialViewFilter } = getInitialUrlData();
    if (searchToggle !== initialViewFilter) {
      dispatch(setSearchToggle(initialViewFilter));
    }
    if (!isEqual(appFilters, initialAppFilters)) {
      dispatch(setAppFilters(initialAppFilters));
    }

    const currentPageNumber = getUrlPageNumber();
    const currentQuery = getUrlParameter('q');
    if (
      currentPageNumber !== searchOptions?.page ||
      currentQuery !== searchOptions?.query
    ) {
      const setSearchInputEvent = new CustomEvent('searchQueryResultsChange', {
        detail: {
          query: currentQuery || searchOptions?.query,
        },
      });
      document.dispatchEvent(setSearchInputEvent);

      search({
        ...searchOptions,
        action: searchActionsEnum.history,
        page: currentPageNumber || searchOptions?.page,
        query: currentQuery || searchOptions?.query,
      });
    }
  };

  function overwriteSearchQuery(query: string) {
    // overwriteSearch always come from new Search in query, so action should be as from searchBar
    dispatch(
      setSearchOptions({
        ...searchOptions,
        action: searchActionsEnum.searchBar,
        query,
      }),
    );
  }

  const search = async (
    args: SearchArgs = {},
    isNewQuerySearch = false,
    familyToggle: string | undefined = undefined,
    brandToggle: boolean | undefined = undefined,
  ) => {
    const {
      suggesterType = null,
      suggesterValue = null,
      searchType = null,
      predefinedIds = [],
      analyticsTitle = null,
      initialRequest,
      additionalFilters = {},
      ...argsOptions
    } = args;
    const options = {
      ...searchOptions,
      ...argsOptions,
    };

    const referrer = window.location.href;

    const isNewSearch =
      options.action === searchActionsEnum.searchBar || isNewQuerySearch;
    const newPageNumber = isNewSearch ? 1 : options.page;
    const resultsPerPage = config.search.resultsPerPage;

    // Redux State update by adding one batch of calls
    batch(() => {
      if (overrideParams != null && !isNewSearch) {
        dispatch(setOverrideParams(null));
        return;
      }

      if (analyticsTitle != null) {
        dispatch(setCurrentCollectionTitle(analyticsTitle));
      }

      dispatch(setSearchOptions(options));

      // Clear filters and sortBy if search query was changed
      if (isNewSearch) {
        dispatch(clearFilters([]));
        dispatch(setIsSearching(true));
        dispatch(setCurrentSortOption(null));
        dispatch(setIsSearchQueryRelaxed(false));
        dispatch(setIsSearchRedirect(false));
        dispatch(setIsBrandSwitch(false));
        if (window.innerWidth < 768) {
          dispatch(toggleFiltersSidebar(false));
        }
        removeCompareParam();
        dispatch(setIsCompareTableShown(false));
        dispatch(resetComparisonFeature());
      }

      if (
        options?.action === searchActionsEnum.customEvent ||
        options.action === searchActionsEnum.pagination
      ) {
        dispatch(setIsSearching(true));
      }

      if (options.action !== searchActionsEnum.pagination) {
        dispatch(setSelectedMaterials([]));
        dispatch(resetComparisonFeature());
      }
    });

    const response = await searchService.search(
      options,
      {
        action: options.action,
        currentPage: newPageNumber,
        resultsPerPage: resultsPerPage,
        customerEmail,
      },
      {
        searchToggle: familyToggle || searchToggle,
        isBrandToggle: isNewSearch ? false : brandToggle ?? isBrandSwitch,
      },
      isNewSearch,
      {
        suggesterType,
        suggesterValue,
        searchType,
      },
      predefinedIds,
      additionalFilters,
    );

    let uniqResults: ProductSearchMaterial[] = [];

    if (response != null) {
      if (predefinedIds && predefinedIds.length > 0) {
        const resList = response?.results || [];
        const idsList = predefinedIds || [];
        // Fix sorting, because after byKey request, order is changed
        uniqResults = sortBy([...resList], function (obj) {
          const index = indexOf(idsList, String(obj?.entityId));
          return index !== -1 ? index : idsList.length;
        });
      } else {
        uniqResults = response?.results;
      }

      const currentPage = response.pagination?.currentPage ?? 1;

      dispatch(
        setResults({
          results: uniqResults,
          contentBlocks: response.contentBlocks,
        }),
      );
      dispatch(setIsSearching(false));

      batch(() => {
        dispatch(
          setPagination({
            currentPage,
            totalPages: response.pagination?.totalPages ?? 0,
            perPage:
              response.pagination?.perPage ?? config.search.resultsPerPage,
            totalResults: response.pagination?.totalResults ?? 0,
          }),
        );

        if (currentSortOption != null && !isNewSearch) {
          if (currentSortOption?.label === '') {
            const responseCurrentOption = response?.sorting?.find(
              (item) =>
                item.field === currentSortOption?.field &&
                item.direction === currentSortOption?.direction,
            );
            if (responseCurrentOption != null) {
              dispatch(setCurrentSortOption(responseCurrentOption));
            }
          }
          setUrlSortOption(currentSortOption);
        }
        dispatch(setSortingList(response.sorting ?? []));
      });

      setGlobalFilters(response.facets);

      const currentUrlPage = getUrlPageNumber();
      const referrerPage = currentUrlPage ? referrer : undefined;

      if (Number(currentUrlPage) !== Number(currentPage)) {
        setUrlPageNumber(currentPage);

        // Trigger 'pageParamsLoaded' function event loaded in gtm-page.phtml
        window.dispatchEvent(
          new CustomEvent('searchPageParams', {
            detail: { referrer: referrerPage },
          }),
        );
      } else if (isNewSearch) {
        window.dispatchEvent(
          new CustomEvent('searchPageParams', {
            detail: { referrer: referrerPage },
          }),
        );
      }

      sessionStorage.setItem('referrer', window.location.href);
    }

    const stockResults = await checkStockAvailability(uniqResults);
    if (Array.isArray(stockResults) && stockResults.length > 0) {
      dispatch(
        setResults({
          results: stockResults,
        }),
      );
    }
  };

  const handleNextPage = (_event: ChangeEvent<unknown>, newPage: number) => {
    window.scrollTo(0, 0);
    search({
      page: newPage,
      action: searchActionsEnum.pagination,
    });
  };

  return (
    <SearchContext.Provider
      value={{
        search,
        handleNextPage,
        overrideBgFilter: searchService.overrideBgFilter,
      }}
    >
      {props.children}
    </SearchContext.Provider>
  );
};

interface SearchContextValue {
  search: (
    args?: SearchArgs,
    isNewQuerySearch?: boolean,
    familyToggle?: string,
    brandToggle?: boolean,
  ) => Promise<void>;
  handleNextPage: (_event: ChangeEvent<unknown>, newPage: number) => void;
  overrideBgFilter?: Record<string, unknown>;
}

const SearchContext = createContext<SearchContextValue>({
  search: () => Promise.resolve(),
  handleNextPage: () => {},
});

export const useSearchContext = () => useContext(SearchContext);
